import { useEffect, useRef, useState, useCallback } from 'react';
import { callOpenAI } from '../../lib/functions';
import { STAGE_CONFIGS } from '../../data/stageConfigs';
import type { StageConfig } from '../../data/stageConfigs';
import type { StageName } from '../../lib/collections';
import { haptic } from '../lib/haptic';
import { useDarkStore } from '../store/darkStore';

interface StageFeedbackProps {
  stageKey: StageName;
}

/** Build a per-stage brief (prompts + this founder's answers) for the LLM. */
function buildStageBrief(
  stageConfig: StageConfig,
  stageAnswers: Record<string, string>,
  explorerName: string,
): string {
  const lines: string[] = [];
  lines.push(`Founder: ${explorerName || 'Explorer'}`);
  lines.push(`Stage: ${stageConfig.number} · ${stageConfig.name}`);
  lines.push('');
  stageConfig.questions.forEach((q) => {
    const v = (stageAnswers[q.id] ?? '').toString().trim();
    if (v) {
      lines.push(`- ${q.prompt}`);
      lines.push(`  → ${v}`);
    }
  });
  return lines.join('\n').slice(0, 2500);
}

function systemPromptFor(stageConfig: StageConfig): string {
  return [
    `You are KoKo, a warm, cheeky co-founder fox mentor for a young founder aged 8–14.`,
    `They have just finished Stage ${stageConfig.number} · ${stageConfig.name}.`,
    `Write 2–3 short sentences of specific feedback on THEIR answers:`,
    `- Celebrate one concrete, specific strength from their answers.`,
    `- Point out exactly one thing they could sharpen next.`,
    `- Tease the next stage in one energising line.`,
    `Rules: British English, one emoji max, never use the word "kindness", no bullet points, no headings, plain prose only.`,
  ].join(' ');
}

const FALLBACK_FEEDBACK =
  "Great run on this stage — the specifics you named stand out more than the average pitch. Next, try to say the same idea in one sentence a friend could repeat back to you. Onwards. ✨";

const QUICK_CONCERNS = [
  'Be more specific about my answers',
  'Focus on what I should improve',
  'Make it shorter',
  'Be more encouraging',
];

export default function StageFeedback({ stageKey }: StageFeedbackProps) {
  const { answers, explorerName, feedbackCache, setFeedbackCache } = useDarkStore();
  const cfg = STAGE_CONFIGS[stageKey];

  const [feedback, setFeedback] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [askingConcern, setAskingConcern] = useState(false);
  const [concern, setConcern] = useState('');
  const calledRef = useRef(false);

  const runFeedback = useCallback((extraContext?: string) => {
    setLoading(true);
    setError(false);
    const stageAnswers = (answers as Record<string, Record<string, string>>)[stageKey] ?? {};
    const inputHash = JSON.stringify(stageAnswers);
    const brief = buildStageBrief(cfg, stageAnswers, explorerName);
    const prompt = extraContext ? `${brief}\n\nThe founder wants you to: ${extraContext}` : brief;
    callOpenAI({ systemPrompt: systemPromptFor(cfg), prompt })
      .then((res) => {
        const text = res.data?.responseText?.trim();
        if (text) {
          setFeedback(text);
          if (!extraContext) setFeedbackCache(stageKey, inputHash, text);
        } else {
          setFeedback(FALLBACK_FEEDBACK);
          setError(true);
        }
      })
      .catch((err) => {
        console.warn('Stage feedback call failed, using fallback:', err);
        setFeedback(FALLBACK_FEEDBACK);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [answers, stageKey, cfg, explorerName, setFeedbackCache]);

  // On mount: use cached feedback if answers haven't changed, otherwise generate.
  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const stageAnswers = (answers as Record<string, Record<string, string>>)[stageKey] ?? {};
    const inputHash = JSON.stringify(stageAnswers);
    const cached = feedbackCache[stageKey];
    if (cached && cached.inputHash === inputHash) {
      setFeedback(cached.text);
      return;
    }
    runFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefreshClick = () => {
    haptic('tap');
    setAskingConcern(true);
    setConcern('');
  };

  const handleConcernSubmit = () => {
    setAskingConcern(false);
    calledRef.current = true;
    runFeedback(concern.trim() || undefined);
    setConcern('');
  };

  const handleCancelConcern = () => {
    setAskingConcern(false);
    setConcern('');
  };

  return (
    <div className="space-y-4">
      {/* KoKo's take */}
      <div className="rounded-2xl border-2 border-amber/40 bg-amber/5 p-5">
        <div className="flex items-start gap-3">
          <div className="text-3xl">🦊</div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="font-marker text-lg text-amber">
                KoKo's take
              </h3>
              {!loading && feedback && !askingConcern && (
                <button
                  type="button"
                  onClick={handleRefreshClick}
                  className="font-marker text-[11px] uppercase tracking-wider text-amber/80 hover:text-amber"
                >
                  ↻ Refresh
                </button>
              )}
            </div>
            {loading && (
              <div className="mt-2 flex items-center gap-1">
                {[0, 150, 300].map((delay) => (
                  <span
                    key={delay}
                    className="koko-dot inline-block h-2 w-2 rounded-full bg-gray-400 dark:bg-ink/70"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
            )}
            {!loading && feedback && !askingConcern && (
              <p className="mt-2 font-gill text-sm leading-relaxed text-gray-800 dark:text-ink/90">
                {feedback}
              </p>
            )}
            {!loading && error && !askingConcern && (
              <div className="mt-2 font-gill text-[11px] italic text-gray-500 dark:text-ink/50">
                (Fallback summary — live KoKo needs a signed-in session.)
              </div>
            )}
            {askingConcern && (
              <div className="mt-3 space-y-3">
                <p className="font-gill text-sm text-amber/90">
                  What's not clicking? KoKo will focus on it.
                </p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_CONCERNS.map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setConcern(chip)}
                      className="rounded-full px-3 py-1 font-gill text-xs transition-colors"
                      style={
                        concern === chip
                          ? { background: '#F59E0B', color: '#0D0F1C' }
                          : { background: 'rgba(245,158,11,0.12)', color: 'rgba(245,158,11,0.8)', border: '1px solid rgba(245,158,11,0.3)' }
                      }
                    >
                      {chip}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={concern}
                  onChange={(e) => setConcern(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConcernSubmit()}
                  placeholder="Or type your own…"
                  className="w-full rounded-xl bg-white/5 px-3 py-2 font-gill text-sm text-white/90 outline-none ring-1 ring-amber/30 placeholder:text-white/30 focus:ring-amber/60"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleConcernSubmit}
                    className="rounded-full px-4 py-1.5 font-marker text-xs font-bold transition-opacity hover:opacity-80"
                    style={{ background: '#F59E0B', color: '#0D0F1C' }}
                  >
                    Ask KoKo →
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelConcern}
                    className="rounded-full px-4 py-1.5 font-marker text-xs transition-opacity hover:opacity-80"
                    style={{ color: 'rgba(245,158,11,0.6)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Worldly Insight */}
      <div className="rounded-2xl border-2 border-violet/40 bg-violet/5 p-5">
        <h3 className="font-marker text-lg text-violet dark:text-violet-glow">
          {cfg.worldlyInsight.title} {cfg.worldlyInsight.emoji}
        </h3>
        <p className="mt-2 font-gill text-sm leading-relaxed text-gray-800 dark:text-ink/90">
          {cfg.worldlyInsight.text}
        </p>
      </div>

      {/* Family Homework */}
      <div className="rounded-2xl border-2 border-mint/30 bg-mint/5 p-5">
        <h3 className="font-marker text-lg text-mint-dim dark:text-mint">
          Family Homework
        </h3>
        <p className="mt-2 font-gill text-sm leading-relaxed text-gray-800 dark:text-ink/90">
          {cfg.familyHomework}
        </p>
      </div>
    </div>
  );
}
